import type { utils } from 'blockly/core';

import { categoryCssConfig, type CategoryIconId } from './categoryCssConfig.js';

type ToolboxItemInfo = utils.toolbox.ToolboxItemInfo;

export interface AddonCategoryDefinition {
  id: string;
  name: string;
  description: string;
  iconId: CategoryIconId;
  colour: number;
  contents: ToolboxItemInfo[];
}

export const ADDON_CATEGORY_CATALOG: readonly AddonCategoryDefinition[] = [
  {
    id: 'led-control',
    name: 'LED Control',
    description: 'Turn LEDs on and off and set pin modes.',
    iconId: 'led',
    colour: 20,
    contents: [
      { kind: 'block', type: 'arduino_pin_mode' },
      { kind: 'block', type: 'arduino_digital_write' },
      { kind: 'block', type: 'arduino_high_low' },
    ],
  },
  {
    id: 'sensors',
    name: 'Sensors',
    description: 'Read values from digital and analog sensors.',
    iconId: 'sensors',
    colour: 260,
    contents: [
      { kind: 'block', type: 'arduino_digital_read' },
      { kind: 'block', type: 'arduino_analog_read' },
    ],
  },
  {
    id: 'actuators',
    name: 'Actuators',
    description: 'Control motors, servos, and other outputs.',
    iconId: 'actuators',
    colour: 140,
    contents: [
      { kind: 'block', type: 'arduino_pin_mode' },
      { kind: 'block', type: 'arduino_digital_write' },
      { kind: 'block', type: 'arduino_analog_write' },
    ],
  },
  {
    id: 'display',
    name: 'Display',
    description: 'Print messages to a serial display or monitor.',
    iconId: 'display',
    colour: 210,
    contents: [
      { kind: 'block', type: 'arduino_serial_begin' },
      { kind: 'block', type: 'arduino_serial_print' },
      { kind: 'block', type: 'arduino_serial_println' },
    ],
  },
  {
    id: 'buzzer',
    name: 'Buzzer',
    description: 'Drive a buzzer or speaker output.',
    iconId: 'buzzer',
    colour: 80,
    contents: [
      { kind: 'block', type: 'arduino_pin_mode' },
      { kind: 'block', type: 'arduino_digital_write' },
      { kind: 'block', type: 'arduino_analog_write' },
    ],
  },
] as const;

const addonById = new Map(ADDON_CATEGORY_CATALOG.map((addon) => [addon.id, addon]));

export function getAddonCategoryDefinition(id: string): AddonCategoryDefinition | undefined {
  return addonById.get(id);
}

export function buildAddonToolboxCategories(
  enabledAddonIds: readonly string[],
): ToolboxItemInfo[] {
  const categories: ToolboxItemInfo[] = [];

  for (const id of enabledAddonIds) {
    const addon = addonById.get(id);
    if (!addon) {
      continue;
    }

    categories.push({
      kind: 'category',
      name: addon.name,
      id: addon.id,
      colour: String(addon.colour),
      cssconfig: categoryCssConfig(addon.iconId),
      contents: addon.contents,
      categorystyle: undefined,
      hidden: undefined,
    });
  }

  return categories;
}
